import { FilterResultMethod } from '@api';
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  fetchKeyValues,
  fetchKeyValuesV3,
} from '@terminal/modules/overview-key-values/services/entity-report-service';
import { fetchWatchlistEntityQuery } from '@terminal/react-query-hooks/watchlist/useWatchlistEntity';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  EntityReturns,
  WatchlistEntities,
} from '@terminal/stores/use-watchlist/watchlist-types';
import { LIVE_PRICE_COLUMN_META_VALUE_FIELD } from '@terminal/views/screener/results/screener-table-utils';
import { fetchFilteredEntities } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/entity-screener-service';
import { mapEntityTagsToDefaultOptions } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/useScreenerEntitiesTags';

export type EntityPlusPrice = {
  prices?: WatchlistEntities['prices'];
  price: number;
  isLoading: WatchlistEntities['isLoading'];
  tag: WatchlistEntities['tag'];
  asset: WatchlistEntities['asset'];
  marketCap?: string;
} & Record<string, FilterResultMethod>;

const QUERY_KEY = 'watchlist-entities-table';

export const useAssetsTableData = (
  entities: WatchlistEntities[],
  pageSize = 15
) => {
  const { isApiScreenerEntitiesV3FlagEnabled, isApiEntityKeyValuesEnabled } =
    useFlags(state => ({
      isApiScreenerEntitiesV3FlagEnabled: state.getFeatureFlagState(
        'DATA-1243-api_screener_entities_v3'
      ),
      isApiEntityKeyValuesEnabled: state.getFeatureFlagState(
        'DATA-1276-api_entity_key_values_v3'
      ),
    }));
  const mockLoadingState = Array.from({ length: 5 }, () => ({
    isLoading: true,
  })) as EntityPlusPrice[];

  return useInfiniteQuery<
    EntityPlusPrice[],
    Error,
    InfiniteData<EntityPlusPrice[]>,
    QueryKey,
    number
  >({
    queryKey: [
      QUERY_KEY,
      entities,
      pageSize,
      isApiScreenerEntitiesV3FlagEnabled,
      isApiEntityKeyValuesEnabled,
    ],
    queryFn: async ({ pageParam }) => {
      const fetchKeyValuesFn = isApiEntityKeyValuesEnabled
        ? fetchKeyValuesV3
        : fetchKeyValues;
      const start = pageParam * pageSize;
      const slice = entities.slice(start, start + pageSize).map(t => t.tag);
      const [settledResults, settledKeyValues, settledScreenerResults] =
        await Promise.all([
          Promise.allSettled(slice.map(tag => fetchWatchlistEntityQuery(tag))),
          Promise.allSettled(slice.map(tag => fetchKeyValuesFn(tag))),
          Promise.allSettled(
            slice.map(tag =>
              fetchFilteredEntities(
                mapEntityTagsToDefaultOptions([tag]),
                isApiScreenerEntitiesV3FlagEnabled
              )
            )
          ),
        ]);

      const results = settledResults.map(r =>
        r.status === 'fulfilled' ? r.value : null
      );
      const keyValuesResults = settledKeyValues.map(r =>
        r.status === 'fulfilled' ? r.value : []
      );
      const entityScreenerResults = settledScreenerResults.map(r =>
        r.status === 'fulfilled' ? r.value : null
      );
      const filtered = results.filter(Boolean) as WatchlistEntities[];

      return filtered.map((data, idx) => {
        const keys = Object.keys(data.returns ?? []) as (keyof EntityReturns)[];
        const mappedReturns = keys.reduce(
          (res, key) => {
            const entityReturn = (data.returns as EntityReturns)[key];
            res[key] = {
              value: entityReturn?.value,
              tooltip: entityReturn?.lastUpdate,
            };
            return res;
          },
          {} as Record<string, FilterResultMethod>
        );
        const keyValues = keyValuesResults[idx];
        const marketCap = keyValues.find(
          item => item.key === 'market_cap'
        )?.value_fmt;

        const entityScreenerPrice =
          entityScreenerResults[idx]?.data?.[0]?.data?.[0]?.[
            LIVE_PRICE_COLUMN_META_VALUE_FIELD
          ]?.value;

        return {
          asset: data.asset,
          tag: data.tag,
          isLoading: false,
          prices: data.prices,
          price: entityScreenerPrice ?? data.prices?.last_value ?? 0,
          marketCap,
          ...mappedReturns,
        } as EntityPlusPrice;
      });
    },
    initialPageParam: 0,
    enabled: !!entities.length,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.length === 0) {
        return null;
      }

      const lastEntity = lastPage.at(-1)?.tag;
      return lastEntity === entities.at(-1)?.tag ? null : lastPageParam + 1;
    },
    placeholderData: () => {
      return {
        pages: [mockLoadingState],
        pageParams: [0],
      };
    },
  });
};
